import React, { useState, useMemo, useEffect } from 'react';
import { Download, Code, Video, Presentation, Quote, X, FileText, Cpu, Database, Copy, Check } from 'lucide-react';

const CustomDialog = ({ isOpen, onClose, title, actions = null, children }) => {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-zinc-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl ring-1 ring-zinc-200 p-4 sm:p-6 w-full max-w-2xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-base font-semibold">{title}</h3>
          <div className="flex items-center gap-1">
          {actions}
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700 rounded-md p-1 hover:bg-zinc-100" aria-label="Close">
            <X size={20} />
          </button>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
};

const AUTHOR_NAME = 'Hussein Mozannar';

const Authors = ({ authors }) => (
  <p className="text-[0.9rem] text-zinc-500 mt-1">
    {authors.map((author, i) => (
      <React.Fragment key={i}>
        {author === AUTHOR_NAME
          ? <span className="text-zinc-800 font-medium">{author}</span>
          : author}
        {i < authors.length - 1 && ', '}
      </React.Fragment>
    ))}
  </p>
);

const Publication = ({ publication }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const links = [
    { key: 'pdf', label: 'PDF', Icon: Download },
    { key: 'code', label: 'Code', Icon: Code },
    { key: 'video', label: 'Video', Icon: Video },
    { key: 'slides', label: 'Slides', Icon: Presentation },
    { key: 'blog', label: 'Blog', Icon: FileText },
    { key: 'model', label: 'Model', Icon: Cpu },
    { key: 'dataset', label: 'Dataset', Icon: Database },
  ];

  const copyBibtex = () => {
    navigator.clipboard?.writeText(publication.bibtex).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div className="group grid grid-cols-1 sm:grid-cols-[4rem_1fr] gap-x-6 gap-y-1 py-6 border-b border-zinc-100 last:border-0">
      <div className="hidden sm:block font-mono text-sm text-zinc-400 pt-1">{publication.year}</div>
      <div className="min-w-0">
        <h3 className="text-[1.05rem] font-semibold leading-snug">{publication.title}</h3>
        <Authors authors={publication.authors} />
        <p className="text-[0.9rem] text-zinc-500 italic">{publication.venue}<span className="sm:hidden">, {publication.year}</span></p>

        <div className="flex flex-wrap gap-2 mt-3">
          {links.map(({ key, label, Icon }) => publication[key] && (
            <a key={key} href={publication[key]} target="_blank" rel="noopener noreferrer" className="chip">
              <Icon size={14} /> {label}
            </a>
          ))}
          {publication.bibtex && (
            <button onClick={() => setIsDialogOpen(true)} className="chip">
              <Quote size={14} /> Cite
            </button>
          )}
        </div>

        {publication.summary && <p className="mt-3 text-zinc-600">{publication.summary}</p>}
      </div>

      <CustomDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        title="BibTeX Citation"
        actions={
          <button onClick={copyBibtex} className="text-zinc-400 hover:text-zinc-700 rounded-md p-1 hover:bg-zinc-100" aria-label="Copy BibTeX">
            {copied ? <Check size={18} /> : <Copy size={18} />}
          </button>
        }
      >
        <pre className="bg-zinc-50 ring-1 ring-zinc-200 p-4 rounded-lg whitespace-pre-wrap break-words text-[0.78rem] leading-relaxed text-zinc-700">
          {publication.bibtex}
        </pre>
      </CustomDialog>
    </div>
  );
};

const PublicationList = ({ publications }) => {
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = useMemo(() => {
    const cats = new Set(publications.map(pub => pub.category));
    return ['All', ...Array.from(cats)];
  }, [publications]);

  const sortedPublications = useMemo(() => {
    // Sort by year descending
    return [...publications];
  }, [publications]);

  const filteredPublications = useMemo(() => {
    if (selectedCategory === 'All') {
      return sortedPublications;
    }
    return sortedPublications.filter(pub => pub.category === selectedCategory);
  }, [sortedPublications, selectedCategory]);

  return (
    <div>
      <p className="page-subtitle"> You can find my full list of publications on my <a href= "https://scholar.google.com/citations?user=XCfZyIkAAAAJ&hl=en"> Google Scholar</a> </p>

      {categories.filter(Boolean).length > 2 && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-3 py-1 rounded-full text-sm font-medium border transition-colors ${
                selectedCategory === category
                  ? 'bg-zinc-900 text-white border-zinc-900'
                  : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      )}
      <div>
        {filteredPublications.map((pub, index) => (
          <Publication key={index} publication={pub} />
        ))}
      </div>
    </div>
  );
};

export default PublicationList;
